/**
 * A tri-state filter component that cycles through three states:
 * - "all": Show all items (no filter applied)
 * - "only": Show only items matching the filter
 * - "exclude": Show all items except those matching the filter
 *
 * @param {Object} props
 * @param {string} props.value - Current filter state ("all", "only", or "exclude")
 * @param {function} props.onChange - Callback when filter state changes
 * @param {string} props.label - Label text for the filter
 * @param {string} [props.onlyLabel] - Optional label for "only" state (default: "Alleen {label}")
 * @param {string} [props.excludeLabel] - Optional label for "exclude" state (default: "Zonder {label}")
 */
export default function TriStateFilter({
  value,
  onChange,
  label,
  onlyLabel,
  excludeLabel,
}) {
  const cycleState = () => {
    // Cycle: all -> only -> exclude -> all
    if (value === "all") {
      onChange("only");
    } else if (value === "only") {
      onChange("exclude");
    } else {
      onChange("all");
    }
  };

  const getDisplayLabel = () => {
    if (value === "only") {
      return onlyLabel || `Alleen ${label.toLowerCase()}`;
    } else if (value === "exclude") {
      return excludeLabel || `Zonder ${label.toLowerCase()}`;
    }
    return label;
  };

  const getIcon = () => {
    if (value === "only") {
      // Checkmark icon for "only"
      return (
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    } else if (value === "exclude") {
      // X icon for "exclude"
      return (
        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    }
    // Empty circle for "all"
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" strokeWidth={2} />
      </svg>
    );
  };

  const getBorderColor = () => {
    if (value === "only") return "border-green-300 bg-green-50";
    if (value === "exclude") return "border-red-300 bg-red-50";
    return "border-gray-300";
  };

  return (
    <button
      type="button"
      onClick={cycleState}
      className={`flex items-center gap-2 px-4 py-2 border rounded cursor-pointer hover:bg-gray-50 transition-colors ${getBorderColor()}`}
      title={`Klik om te wisselen: Alles → Alleen → Zonder`}
    >
      {getIcon()}
      <span className="text-sm text-gray-700">{getDisplayLabel()}</span>
    </button>
  );
}
